// /var/www/afra/web-design.rampagesoft.com/my-app/src/page/Content.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BreadCrumb } from 'primereact/breadcrumb';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Paginator } from 'primereact/paginator';
import { fetchPosts, fetchPostImage } from '../services/api';
import { stripHtml, truncateText, createUrlFriendlyString, formatDate, getImageUrl } from '../utility/utils';
import '../css/Content.scss';
import 'primeicons/primeicons.css';

export default function Content() {
  const [posts, setPosts] = useState([]);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [first, setFirst] = useState(0);
  const rowsPerPage = 15;
  const defaultImage = 'https://afaa.website/s/ff6970.png';

  useEffect(() => {
    async function fetchContentList() {
      try {
        setLoading(true);
        setError(null);
        //console.log(`[Content.js] Fetching page ${currentPage}`);
        const offset = currentPage * rowsPerPage;
        const data = await fetchPosts(rowsPerPage, offset);
        const fetchedPosts = data.results || [];

        const validPosts = fetchedPosts.filter(post => post && post.Home_ID);
        if (validPosts.length !== fetchedPosts.length) {
          console.warn('[Content.js] Some posts were filtered out due to missing Home_ID.');
        }
        setPosts(validPosts);
        setTotalRecords(data.total_count || 0);

        const imageResults = await Promise.all(
          validPosts.map(async post => {
            let img = defaultImage;
            try {
              const imageData = await fetchPostImage(post.Home_ID);
              const rawImg = imageData.results?.[0]?.ImgHome_Img || '';
              img = getImageUrl(rawImg, defaultImage);
            } catch (imgError) {
              console.warn(`[Content.js] Failed to fetch image for Home_ID ${post.Home_ID}: ${imgError.message}`);
            }
            return { id: post.Home_ID, imgUrl: img };
          })
        );
        setImages(Object.fromEntries(imageResults.map(r => [r.id, r.imgUrl])));
      } catch (e) {
        console.error('[Content.js] Failed to fetch content list:', e.message);
        setError('Unable to load content. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
    fetchContentList();
  }, [currentPage]);

  const onPageChange = event => {
    setFirst(event.first);
    setCurrentPage(event.page);
    window.scrollTo(0, 0);
  };

  const breadcrumbItems = [
    { label: 'Home', url: '/', icon: 'pi pi-home' },
    { label: 'Content', url: '/content', icon: 'pi pi-folder', disabled: true },
  ];

  if (loading) return <div className="content__loading"><ProgressSpinner /> <p>Loading articles...</p></div>;
  if (error) return <div className="content__error">{error}</div>;
  if (posts.length === 0 && !loading) return <div className="content__empty">No content available</div>;

  return (
    <section>
      <div data-mode="container">
        <BreadCrumb model={breadcrumbItems} className="content__breadcrumb" />
        <div className="content">
          {posts.map(post => {
            if (!post || !post.Home_ID) return null;

            const cleanedTitle = stripHtml(post.Home_Sub || 'Untitled');
            const truncatedTitle = truncateText(cleanedTitle, 50);
            const cleanedDescription = stripHtml(post.Home_Short || '');
            const truncatedDescription = truncateText(cleanedDescription, 200);
            const urlFriendlyName = createUrlFriendlyString(cleanedTitle || 'untitled');

            return (
              <article key={post.Home_ID} className="content__item">
                <Link to={`/content/${post.Home_ID}/${urlFriendlyName}`}>
                  {images[post.Home_ID] && (
                    <figure className="content__image-container">
                      <img
                        src={images[post.Home_ID]}
                        alt={cleanedTitle}
                        className="content__image"
                        onError={e => {
                          console.warn(`[Content.js] Failed to load image: ${images[post.Home_ID]}`);
                          e.target.src = defaultImage;
                        }}
                      />
                    </figure>
                  )}
                  <header className="content__header">
                    <h2 className="content__title">{truncatedTitle}</h2>
                    <div className="content__meta">
                      <span className="content__meta-item">
                        <i className="pi pi-calendar"></i>
                        {formatDate(post.Home_Date)}
                      </span>
                      <span className="content__meta-item">
                        <i className="pi pi-eye"></i>
                        {post.Home_Visitors || 0}
                      </span>
                    </div>
                  </header>
                  <section className="content__summary">
                    <p>{truncatedDescription}</p>
                  </section>
                </Link>
              </article>
            );
          })}
        </div>
        {totalRecords > rowsPerPage && (
          <Paginator
            first={first}
            rows={rowsPerPage}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            className="content__paginator"
          />
        )}
      </div>
    </section>
  );
}